/** @format */
import { inject, Injectable } from '@angular/core';
import { Range, ValidatablePlan } from 'common-typescript';
import { dateUtils, ISO_LOCAL_DATE_FORMAT } from 'common-typescript/constants';
import {
    CourseUnit,
    CourseUnitAttainment,
    CustomCourseUnitAttainment,
    CustomStudyDraft,
    LocalId,
    OtmId,
    StudyPeriod,
    StudyPeriodLocator,
    StudyRight,
    StudyYear,
} from 'common-typescript/types';
import moment from 'moment';
import { combineLatest, map, Observable, shareReplay } from 'rxjs';
import { AppErrorHandler } from 'sis-components';
import { SelectedValidatablePlanService } from '../service/selected-validatable-plan.service';
import { TimelineControlsService } from '../service/timeline-controls.service';
import { sortTimelineItems } from '../sorting-utils';
import {
    isTimelineCourseUnit,
    isTimelineCustomCourseUnitAttainment,
    isTimelineCustomStudyDraft,
    StudyPeriodData,
    StudyTermData,
    TimelineCourseUnit,
    TimelineCustomCourseUnitAttainment,
    TimelineCustomStudyDraft,
    TimelineItem,
} from '../timing.types';
import { SelectedPlanStudyRightService } from './selected-plan-study-right.service';
import { TimelineStudyYearsService } from './timeline-study-years.service';

@Injectable()
export class StudyPeriodDataService {
    private readonly selectedValidatablePlanService = inject(SelectedValidatablePlanService);
    private readonly selectedPlanStudyRightService = inject(SelectedPlanStudyRightService);
    private readonly timelineStudyYearsService = inject(TimelineStudyYearsService);
    private readonly timelineControlsService = inject(TimelineControlsService);
    private readonly appErrorHandler: AppErrorHandler = inject(AppErrorHandler);

    /**
     * If there is no {@link SelectedValidatablePlanService.selectedValidatablePlan$}, an empty array is returned.
     */
    readonly studyTermData$: Observable<readonly StudyTermData[]> = combineLatest([
        this.timelineStudyYearsService.studyYears$,
        this.selectedValidatablePlanService.selectedValidatablePlan$,
        this.selectedPlanStudyRightService.studyRight$,
        this.timelineControlsService.showEmptySummerPeriods$,
        this.timelineControlsService.showPastStudyTerms$,
    ]).pipe(
        map(([studyYears, validatablePlan, studyRight, showSummerPeriod, showPastStudyTerms]) =>
            // We can skip the whole "no selected plan" branch,
            // as TimelineStudyYearsService.studyYears$ won't return any study years anyway.
            validatablePlan ? createStudyTermData(studyYears, validatablePlan, studyRight, showSummerPeriod, showPastStudyTerms) : [],
        ),
        this.appErrorHandler.defaultErrorHandler(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
}

function createStudyTermData(
    studyYears: readonly StudyYear[],
    validatablePlan: ValidatablePlan,
    studyRight: StudyRight | null,
    showSummerPeriod: boolean,
    showPastStudyTerms: boolean,
): readonly StudyTermData[] {
    const { endDate: studyRightEnd } = studyRight?.valid ?? {};

    const studyTermData: StudyTermData[] = [];

    // ValidatablePlan's methods are not necessarily that optimized, so let's loop them only once
    const courseUnitsInPlan: readonly CourseUnit[] = validatablePlan.getAllCourseUnitsInPlan();
    const periodLocatorsPerCourseUnitId = new Map<OtmId, ReadonlySet<StudyPeriodLocator>>();
    const attainmentPerCourseUnitId = new Map<OtmId, CourseUnitAttainment | null>();
    for (const courseUnit of courseUnitsInPlan) {
        periodLocatorsPerCourseUnitId.set(courseUnit.id, new Set<StudyPeriodLocator>(validatablePlan.getPlannedPeriods(courseUnit.id)));
        attainmentPerCourseUnitId.set(courseUnit.id, validatablePlan.getCourseUnitAttainment(courseUnit.id));
    }
    const customCourseUnitAttainmentsInPlan: readonly CustomCourseUnitAttainment[] =
        validatablePlan.getAllCustomCourseUnitAttainmentsInPlan();
    const customStudyDraftsInPlan: readonly CustomStudyDraft[] = validatablePlan.plan.customStudyDrafts ?? [];

    studyYears.forEach(year =>
        year.studyTerms?.forEach(term => {
            // Filter out past study terms if the user has not chosen to display them
            if (!showPastStudyTerms) {
                const termEndDate = term.valid?.endDate;
                if (termEndDate && moment().isSameOrAfter(termEndDate, 'day')) {
                    return;
                }
            }

            const periods: StudyPeriodData[] = [];
            let totalStudyTermAttainedCredits = 0;
            let totalStudyTermTimedCredits = Range.zero();

            term.studyPeriods?.forEach(period => {
                const timelineItems: readonly TimelineItem[] = createTimelineItems(
                    period,
                    courseUnitsInPlan,
                    periodLocatorsPerCourseUnitId,
                    attainmentPerCourseUnitId,
                    customCourseUnitAttainmentsInPlan,
                    customStudyDraftsInPlan,
                );

                const studyPeriodAttainedCredits = calculateTotalAttainedCredits(timelineItems, attainmentPerCourseUnitId);
                const studyPeriodTimedCredits = calculateTotalTimedCredits(
                    timelineItems,
                    attainmentPerCourseUnitId,
                    periodLocatorsPerCourseUnitId,
                );
                // Update the totals before filtering out visible periods, as the hidden periods can affect the totals
                totalStudyTermAttainedCredits += studyPeriodAttainedCredits;
                totalStudyTermTimedCredits = totalStudyTermTimedCredits.add(studyPeriodTimedCredits);

                // summer period having timeline items are always visible
                if (!period.visibleByDefault && !showSummerPeriod && !timelineItems.length) {
                    return;
                }

                const periodData: StudyPeriodData = {
                    locator: period.locator,
                    name: period.name,
                    timelineItems,
                    valid: period.valid,
                    attainedCredits: studyPeriodAttainedCredits,
                    timedCredits: { ...studyPeriodTimedCredits },
                };

                periods.push(periodData);
            });
            const studyRightExpired =
                !!studyRight && !!studyRightEnd && dateUtils.createMoment(term.valid.startDate).isSameOrAfter(studyRightEnd, 'day');
            studyTermData.push({
                name: term.name,
                startYear: moment(term.valid.startDate).year(),
                valid: term.valid,
                studyRightExpiredInfo: studyRightExpired
                    ? {
                          studyRightExpiredDateInclusive: dateUtils
                              .createMoment(studyRightEnd)
                              .subtract(1, 'day')
                              .format(ISO_LOCAL_DATE_FORMAT),
                          studyRightId: studyRight.id,
                          isFirstTermAfterExpiry:
                              studyRightExpired &&
                              !studyTermData.some(previousTerm => previousTerm.studyRightExpiredInfo?.isFirstTermAfterExpiry),
                      }
                    : undefined,
                attainedCredits: totalStudyTermAttainedCredits,
                timedCredits: { ...totalStudyTermTimedCredits },
                periods,
            });
        }),
    );

    return studyTermData;
}

export interface CodedTimelineItem {
    readonly id: OtmId | LocalId;
    readonly code: string | null;
    readonly item: TimelineCourseUnit | TimelineCustomCourseUnitAttainment | TimelineCustomStudyDraft;
}

function createTimelineItems(
    studyPeriod: StudyPeriod,
    courseUnitsInPlan: readonly CourseUnit[],
    periodLocatorsPerCourseUnitId: ReadonlyMap<OtmId, ReadonlySet<StudyPeriodLocator>>,
    attainmentPerCourseUnitId: ReadonlyMap<OtmId, CourseUnitAttainment | null>,
    customCourseUnitAttainmentsInPlan: readonly CustomCourseUnitAttainment[],
    customStudyDraftsInPlan: readonly CustomStudyDraft[],
): TimelineItem[] {
    const codedTimelineItems: CodedTimelineItem[] = [];

    for (const courseUnit of courseUnitsInPlan) {
        const courseUnitAttainment: CourseUnitAttainment | null = attainmentPerCourseUnitId.get(courseUnit.id);

        // Place course unit based on attainment date instead of originally planned study periods
        if (
            courseUnitAttainment
                ? dateUtils.rangeContains(courseUnitAttainment.attainmentDate, studyPeriod.valid)
                : periodLocatorsPerCourseUnitId.get(courseUnit.id).has(studyPeriod.locator)
        ) {
            // Course unit has been explicitly timed to (or attained during) this period
            codedTimelineItems.push({
                id: courseUnit.id,
                code: courseUnit.code,
                item: <TimelineCourseUnit>{
                    id: courseUnit.id,
                    type: 'CourseUnit',
                    placedInPeriods: courseUnitAttainment
                        ? [studyPeriod.locator]
                        : Array.from(periodLocatorsPerCourseUnitId.get(courseUnit.id)),
                    courseUnit,
                },
            });
        }
    }

    for (const customCourseUnitAttainment of customCourseUnitAttainmentsInPlan) {
        if (dateUtils.rangeContains(customCourseUnitAttainment.attainmentDate, studyPeriod.valid)) {
            codedTimelineItems.push({
                id: customCourseUnitAttainment.id,
                code: customCourseUnitAttainment.code,
                item: <TimelineCustomCourseUnitAttainment>{
                    id: customCourseUnitAttainment.id,
                    type: 'CustomCourseUnitAttainment',
                    placedInPeriods: [studyPeriod.locator],
                    customCourseUnitAttainment,
                },
            });
        }
    }

    for (const customStudyDraft of customStudyDraftsInPlan) {
        if (customStudyDraft.plannedPeriods.includes(studyPeriod.locator)) {
            codedTimelineItems.push({
                id: customStudyDraft.id,
                code: null,
                item: <TimelineCustomStudyDraft>{
                    id: customStudyDraft.id,
                    type: 'CustomStudyDraft',
                    placedInPeriods: customStudyDraft.plannedPeriods,
                    customStudyDraft,
                },
            });
        }
    }

    // sort course units and custom course unit attainments by code
    sortTimelineItems(codedTimelineItems);

    return codedTimelineItems.map(({ item }) => item);
}

function calculateTotalAttainedCredits(
    timelineItems: readonly TimelineItem[],
    attainmentPerCourseUnitId: ReadonlyMap<OtmId, CourseUnitAttainment | null>,
): number {
    let totalCredits = 0;

    for (const item of timelineItems) {
        if (isTimelineCourseUnit(item) && !!attainmentPerCourseUnitId.get(item.courseUnit.id)) {
            totalCredits += attainmentPerCourseUnitId.get(item.courseUnit.id).credits;
        } else if (isTimelineCustomCourseUnitAttainment(item)) {
            totalCredits += item.customCourseUnitAttainment.credits;
        }
    }

    return totalCredits;
}

function calculateTotalTimedCredits(
    timelineItems: readonly TimelineItem[],
    attainmentPerCourseUnitId: ReadonlyMap<OtmId, CourseUnitAttainment | null>,
    plannedPeriodsPerCourseUnitId: ReadonlyMap<OtmId, ReadonlySet<StudyPeriodLocator>>,
): Range {
    let totalCredits = Range.zero();

    for (const item of timelineItems) {
        if (isTimelineCourseUnit(item) && !attainmentPerCourseUnitId.get(item.courseUnit.id)) {
            totalCredits = totalCredits.add(
                Range.divideRange(new Range(item.courseUnit.credits), plannedPeriodsPerCourseUnitId.get(item.courseUnit.id).size),
            );
        } else if (isTimelineCustomStudyDraft(item)) {
            totalCredits = totalCredits.add(
                Range.divideRange(new Range(item.customStudyDraft.credits), item.customStudyDraft.plannedPeriods.length),
            );
        }
    }

    return totalCredits;
}
